import styled, { css, keyframes, type DefaultTheme } from "styled-components";
import { borderRadius, borderWidth, shadows, spacing, typographyMixin } from "@magicbi/ui-library";
import { IconButton } from "../../atoms/IconButton";
import { ProgressBar } from "../../atoms/ProgressBar";
import type { ToastAppearance, ToastKind, ToastRadius } from "./Toast.types";

const enter = keyframes`
  from { opacity: 0; transform: translateY(0.5rem); }
  to { opacity: 1; transform: none; }
`;

const tintOf = (theme: DefaultTheme, kind: ToastKind): { fill: string; ink: string } => {
  switch (kind) {
    case "success":
      return { fill: theme.colors.success[50], ink: theme.colors.success.text };
    case "warning":
      return { fill: theme.colors.warning[50], ink: theme.colors.warning.text };
    case "error":
      return { fill: theme.colors.error[50], ink: theme.colors.error.text };
    default:
      return { fill: theme.colors.background.tertiary, ink: theme.colors.interactive.primary };
  }
};

/** Inverse pill: the icon sits on a solid fill (Flow-07: a green disc with a white check). */
const inverseBadgeOf = (theme: DefaultTheme, kind: ToastKind): string => {
  switch (kind) {
    case "success":
      return theme.colors.success[500];
    case "warning":
      return theme.colors.warning[500];
    case "error":
      return theme.colors.error[500];
    default:
      return theme.colors.interactive.primary;
  }
};

const borderOf = (theme: DefaultTheme, kind: ToastKind): string =>
  kind === "warning" ? theme.colors.warning[500] : kind === "error" ? theme.colors.error[500] : theme.colors.border.stroke;

type Look = { $appearance?: ToastAppearance };
const isInverse = ({ $appearance }: Look): boolean => $appearance === "inverse";

/** Prototype `.nt-toast`: 32px icon · text · 32px dismiss, on a raised card. */
export const Card = styled.div<{ $kind: ToastKind; $radius?: ToastRadius } & Look>`
  pointer-events: auto;
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr) 2rem;
  column-gap: ${spacing[3]};
  align-items: start;
  padding: ${spacing[3]} ${spacing[2]} ${spacing[3]} ${spacing[3]};
  background: ${({ theme }) => theme.colors.surface.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme, $kind }) => borderOf(theme, $kind)};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.component.popover};
  animation: ${enter} 0.18s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* Flow-07 "Saved" toast: a dark pill, one row, 52px high. */
  ${(props) =>
    isInverse(props) &&
    css`
      display: flex;
      align-items: center;
      gap: ${spacing[2.5]};
      min-height: 3.25rem;
      padding: 0 ${spacing[2]} 0 ${spacing[4]};
      background: ${props.theme.colors.background.inverse};
      color: ${props.theme.colors.text.inverse};
      border: 0;
      border-radius: ${borderRadius.full};
      box-shadow: ${shadows.component.popover};
      max-width: calc(100vw - 2rem);
    `}

  ${({ $radius }) =>
    $radius &&
    css`
      border-radius: ${borderRadius[$radius]};
    `}
`;

export const IconBadge = styled.span<{ $kind: ToastKind } & Look>`
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: ${borderRadius.full};
  ${({ theme, $kind }) => {
    const tint = tintOf(theme, $kind);
    return css`
      background: ${tint.fill};
      color: ${tint.ink};
    `;
  }}

  svg {
    width: 1rem;
    height: 1rem;
  }

  ${(props) =>
    isInverse(props) &&
    css`
      width: 1.375rem;
      height: 1.375rem;
      flex: none;
      background: ${inverseBadgeOf(props.theme, props.$kind)};
      color: ${props.theme.colors.text.inverse};

      svg {
        width: 0.75rem;
        height: 0.75rem;
      }
    `}
`;

export const Content = styled.div<Look>`
  min-width: 0;
  padding-top: ${spacing[1]};

  ${(props) =>
    isInverse(props) &&
    css`
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      column-gap: ${spacing[2.5]};
      padding: ${spacing[1]} 0;
    `}
`;

export const Title = styled.p<Look>`
  margin: 0;
  ${typographyMixin("body", 2)}
  overflow-wrap: anywhere;
`;

export const Body = styled.p<Look>`
  margin: ${spacing[0.5]} 0 0;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
  ${(props) =>
    isInverse(props) &&
    css`
      margin: 0;
      color: ${props.theme.colors.text.inverse};
      opacity: 0.75;
    `}
`;

export const Bar = styled(ProgressBar)`
  margin-top: ${spacing[2]};
`;

export const Footer = styled.div<Look>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[3]};
  margin-top: ${spacing[1]};

  ${(props) =>
    isInverse(props) &&
    css`
      flex-wrap: nowrap;
      gap: ${spacing[2]};
      margin-top: 0;
    `}
`;

/** Prototype `.linkbtn`: the one action, as a text link. */
export const ActionLink = styled.button<Look & { $emphasis?: boolean }>`
  padding: 0;
  border: 0;
  background: none;
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.link};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.linkHover};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
    border-radius: ${borderRadius.sm};
  }

  /* Inverse: a pill button. The first is tonal, the second (emphasis) is light. */
  ${(props) =>
    isInverse(props) &&
    css`
      height: 2.125rem;
      padding: 0 ${spacing[3]};
      border-radius: ${borderRadius.full};
      background: ${props.$emphasis ? props.theme.colors.surface.primary : props.theme.colors.background.inverseHover};
      color: ${props.$emphasis ? props.theme.colors.interactive.primary : props.theme.colors.text.inverse};
      white-space: nowrap;

      &:hover {
        color: ${props.$emphasis ? props.theme.colors.interactive.primary : props.theme.colors.text.inverse};
        text-decoration: none;
        opacity: 0.9;
      }

      &:focus-visible {
        outline-color: ${props.theme.colors.text.inverse};
        border-radius: ${borderRadius.full};
      }
    `}
`;

/** IconButton, recoloured from the theme so it reads on dark surfaces too. */
export const Dismiss = styled(IconButton)<Look>`
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${borderRadius.lg};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surface.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: 0;
  }

  ${(props) =>
    isInverse(props) &&
    css`
      flex: none;
      color: ${props.theme.colors.text.inverse};
      opacity: 0.8;

      &:hover:not(:disabled) {
        background: ${props.theme.colors.background.inverseHover};
        color: ${props.theme.colors.text.inverse};
        opacity: 1;
      }

      &:focus-visible {
        outline-color: ${props.theme.colors.text.inverse};
      }
    `}
`;
